import { RxCollection, RxDatabase, RxDumpCollection } from "rxdb"
import {
  AppAllDBCollections,
  AppBaseDBCollections,
  AppChangesDBCollections,
  AppCommonDBCollections,
  formatCSIDBName,
  getChangesDatabase,
  getCommonDatabase,
  getDatabase,
} from "./databases"

/**
 * Schema hashes for dump-inserts.
 */
export const schemaHashes: Record<keyof AppBaseDBCollections, string> = {
  attachment: "",
  attachment_data: "",
  attribute_char: "",
  attribute_char_value: "",
  attribute_numeric: "",
  attribute_numeric_value: "",
  attribute_select: "",
  attribute_select_options: "",
  attribute_select_value: "",
  attribute_temporal: "",
  attribute_temporal_value: "",
  corrective_work_on_item: "",
  corrective_work_item_comments: "",
  identifier: "",
  issue_on_item_actions: "",
  issue_on_item_comments: "",
  issue_priority: "",
  issues_on_item: "",
  item: "",
  item_class: "",
  item_model: "",
  item_work_orders: "",
  regular_work_on_item: "",
  regular_work_item_comments: "",
  work_domain_workspace: "",
  work_order_corrective_item: "",
  work_order_regular_item: "",
  work_order_corrective_comments: "",
  work_order_regular_comments: "",
  work_order_priority: "",
}

export const schemaChangesHashes: Record<keyof AppChangesDBCollections, string> = {
  unsubmitted_attachment: "",
  unsubmitted_work: "",
  unsubmitted_work_issue: "",
}

export const schemaCommonHashes: Record<keyof AppCommonDBCollections, string> = {
  example_item: "",
  user: "",
  user_avatar: "",
  user_role: "",
  work_domain_global: "",
  workspace: "",
}

/**
 * Helper to construct dumps for use with `importDump()`.
 *
 * @param collectionName
 * @param documents
 */
export function constructImportDump<DocumentType>(
  collectionName: keyof AppBaseDBCollections,
  documents: DocumentType[]
): RxDumpCollection<DocumentType> {
  return {
    docs: documents,
    name: collectionName as string,
    passwordHash: undefined,
    schemaHash: schemaHashes[collectionName],
  }
}

export function constructChangesImportDump<DocumentType>(
  collectionName: keyof AppChangesDBCollections,
  documents: DocumentType[]
): RxDumpCollection<DocumentType> {
  return {
    docs: documents,
    name: collectionName as string,
    passwordHash: undefined,
    schemaHash: schemaChangesHashes[collectionName],
  }
}

export function constructCommonImportDump<DocumentType>(
  collectionName: keyof AppCommonDBCollections,
  documents: DocumentType[]
): RxDumpCollection<DocumentType> {
  return {
    docs: documents,
    name: collectionName as string,
    passwordHash: undefined,
    schemaHash: schemaCommonHashes[collectionName],
  }
}

/**
 * Initiate schema hashes
 */

export async function initiateBaseSchemaHashes() {
  const dbName = await formatCSIDBName("test_db")
  await initiateHashesGeneral(schemaHashes, getDatabase, dbName)
}

export async function initiateChangesSchemaHashes() {
  const dbName = await formatCSIDBName("test_changes_db")
  await initiateHashesGeneral(schemaChangesHashes, getChangesDatabase, dbName)
}

export async function initiateCommonSchemaHashes() {
  const dbName = "test_common_db"
  await initiateHashesGeneral(schemaCommonHashes, getCommonDatabase, dbName)
}

async function initiateHashesGeneral<
  DBCollection extends AppAllDBCollections,
  DB extends RxDatabase<DBCollection> = RxDatabase<DBCollection>,
>(
  hashesList: Record<keyof DBCollection, string>,
  dbFunc: (name: string) => Promise<DB>,
  dbName: string
) {
  // Check if schema hashes must be initialized
  if (Object.values(hashesList).includes("")) {
    // Use the test database to get hashes, since it will not contain
    const testDB = await dbFunc(dbName)

    for (const collection of Object.values(testDB.collections)) {
      const typedCollection = collection as RxCollection
      const collectionExport = await typedCollection.exportJSON()
      hashesList[collectionExport.name as keyof DBCollection] = collectionExport.schemaHash
    }
  }
}
